import { create } from "zustand";
import { api, setToken, User } from "./api";
import { useChatStore } from "./chatStore";
export type AuthUser = Pick<
  User,
  "id" | "organization_id" | "display_name" | "role"
> & {
  email?: string;
  organization_name?: string | null;
  totp_enabled?: boolean;
};

interface AuthResponse {
  access_token: string;
  user_id: number;
  organization_id: number;
  email?: string;
  display_name: string;
  role: User["role"];
  totp_enabled: boolean;
}

interface ImpersonatorSession {
  token: string;
  user: AuthUser;
}

/** Thrown by `login` when the account has 2FA enabled and we still need a code. */
export class TotpRequiredError extends Error {
  constructor() {
    super("totp_required");
    this.name = "TotpRequiredError";
  }
}

/** Thrown by `login` when the supplied 2FA code didn't verify. */
export class TotpInvalidError extends Error {
  constructor() {
    super("invalid_totp_code");
    this.name = "TotpInvalidError";
  }
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  impersonator: ImpersonatorSession | null;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  impersonate: (userId: number) => Promise<void>;
  stopImpersonating: () => void;
  logout: () => void;
  hydrate: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  token: localStorage.getItem("cf_token"),
  user: safeParse<AuthUser>(localStorage.getItem("cf_user")),
  impersonator: safeParse<ImpersonatorSession>(localStorage.getItem("cf_impersonator")),

  login: async (email, password, totpCode) => {
    try {
      const resp = await api.post<AuthResponse>("/auth/login", {
        email,
        password,
        ...(totpCode ? { totp_code: totpCode } : {}),
      });

      clearSessionCache();
      setToken(resp.access_token);
      const user = userFromAuthResponse(resp, email);
      localStorage.setItem("cf_user", JSON.stringify(user));
      localStorage.removeItem("cf_impersonator");
      set({ token: resp.access_token, user, impersonator: null });
    } catch (e: any) {
      // Map the backend's sentinel errors so callers can drive the UI.
      const msg = e?.message;
      if (msg === "totp_required") throw new TotpRequiredError();
      if (msg === "invalid_totp_code") throw new TotpInvalidError();
      throw e;
    }
  },

  impersonate: async (userId) => {
    const current = get();
    if (!current.token || !current.user) return;
    const resp = await api.post<AuthResponse>(`/auth/impersonate/${userId}`);
    const impersonator = current.impersonator ?? {
      token: current.token,
      user: current.user,
    };
    const user = userFromAuthResponse(resp);
    clearSessionCache();
    setToken(resp.access_token);
    localStorage.setItem("cf_user", JSON.stringify(user));
    localStorage.setItem("cf_impersonator", JSON.stringify(impersonator));
    set({ token: resp.access_token, user, impersonator });
  },

  stopImpersonating: () => {
    const impersonator = get().impersonator;
    if (!impersonator) return;
    clearSessionCache();
    setToken(impersonator.token);
    localStorage.setItem("cf_user", JSON.stringify(impersonator.user));
    localStorage.removeItem("cf_impersonator");
    set({ token: impersonator.token, user: impersonator.user, impersonator: null });
  },

  logout: () => {
    clearSessionCache();
    set({ token: null, user: null, impersonator: null });
  },

  hydrate: async () => {
    const token = localStorage.getItem("cf_token");
    if (!token) return;
    try {
      const me = await api.get<User>("/auth/me");
      const user: AuthUser = {
        id: me.id,
        organization_id: me.organization_id,
        organization_name: me.organization_name,
        display_name: me.display_name,
        role: me.role,
        email: me.email,
        totp_enabled: me.totp_enabled,
      };
      localStorage.setItem("cf_user", JSON.stringify(user));
      set({ token, user });
    } catch {
      clearSessionCache();
      set({ token: null, user: null, impersonator: null });
    }
  },

  refreshUser: async () => {
    if (!get().token) return;
    const me = await api.get<User>("/auth/me");
    const user: AuthUser = {
      id: me.id,
      organization_id: me.organization_id,
      organization_name: me.organization_name,
      display_name: me.display_name,
      role: me.role,
      email: me.email,
      totp_enabled: me.totp_enabled,
    };
    localStorage.setItem("cf_user", JSON.stringify(user));
    set({ user });
  },
}));

function userFromAuthResponse(resp: AuthResponse, fallbackEmail?: string): AuthUser {
  return {
    id: resp.user_id,
    organization_id: resp.organization_id,
    display_name: resp.display_name,
    role: resp.role,
    email: resp.email ?? fallbackEmail,
    totp_enabled: resp.totp_enabled,
  };
}

function safeParse<T>(s: string | null): T | null {
  if (!s) return null;
  try { return JSON.parse(s) as T; } catch { return null; }
}

function clearSessionCache() {
  useChatStore.getState().reset();
  setToken(null);
  localStorage.removeItem("cf_user");
  localStorage.removeItem("cf_impersonator");
}
